import hash from "object-hash";
import { Hexagen, Player, Statistic } from "../types";
import { videoDebugLog } from "../utils/videoDebugLog";
import {
  calculateNewWorldCoordinates,
  getInitialCoordinates,
} from "./stateUtils";
import { INotification } from "../types";

export const hexWidth = 210;
export const hexHeight = 145;

export interface BattleState {
  uid: string | null;
  result: "win" | "lose" | null;
  story: string | null;
  attacking: {
    player: string;
    hand: string[] | null;
    main: string | null;
    secondary: string | null;
    third: string | null;
  } | null;
  defending: {
    player: string;
    hand: string[] | null;
    main: string | null;
    secondary: string | null;
    third: string | null;
  } | null;
}

// 1 - ADD PROPERTY TO STATE INTERFACE
export interface AppState {
  hash: string;
  player: Player | null;
  isPlayerProfilePopupOpen: boolean;
  isConfirmDeletePopupOpen: boolean;
  isLoginPopupOpen: boolean;
  isHexInfoPopupOpen: boolean;
  isHexGeneratePopupOpen: boolean;
  isPlayerInfoPopupOpen: boolean;
  isAboutGamePopupOpen: boolean;
  isMenuPopupOpen: boolean;
  isGeneratedAwardPopupOpen: boolean;
  isMostViewedPopupOpen: boolean;
  isGlobalChatPopupOpen: boolean;
  isSearchPopupOpen: boolean;
  isRedeemPopupOpen: boolean;
  isReportPopupOpen: boolean;
  isNFTAlertPopupOpen: boolean;
  isNotificationsPopupOpen: boolean;
  isNftPopupOpen: boolean;
  isNFTConfirmMintingPopupOpen: boolean;
  statistic: Statistic;
  worldX: number;
  worldY: number;
  worldScale: number;
  selectedHexCoord: { x: number; y: number } | null;
  selectedHexInfo: Hexagen | null;
  selectedHexExtra: {
    hasUpvote: boolean;
    creator: Player | null;
    owner: Player | null;
  } | null;
  generatingNowHexs: { x: number; y: number }[];
  regeneratingNowHexs: { x: number; y: number }[];
  convertingNowHexs: { x: number; y: number }[];
  videoGeneratingNowHexs: { x: number; y: number }[];
  hasVideo: { x: number; y: number }[];
  isWorldDragging: boolean;
  selectedPlayerUID: string | null;
  booleanMap: { [key: string]: boolean };
  tileZIndexes: { [key: string]: number };
  notifications: INotification[];
  ratings: {
    mostViewed: Hexagen[];
    mostViewedLastWeek: Hexagen[];
    lastCreated: Hexagen[];
    topPlayers: Player[];
    mostUpvotedLastWeek: Hexagen[];
    highestPointsLastWeek: Hexagen[];
  };
  isMenuLeftOpen: boolean;
  regeneratedHexagons: { x: number; y: number; createdAt: number }[];
  generatedHexagons: { x: number; y: number; createdAt: number }[];
  awardPopupCoordinates: { x: number; y: number } | null;
  awardPopupQueue: { x: number; y: number }[];
  battle: BattleState | null;
  isBattleFieldPopupOpen: boolean;
  /** Reverse natural map scrolling on both axes. Default false follows macOS natural scrolling. */
  invertMapPan: boolean;
  /** When true, use 1024px tiles (@big). When false (default), use 512px tiles. */
  highQuality: boolean;
  /** Extra hex rows/cols beyond visible area (0 = tight, 1 = one row/col buffer). */
  hexGridBuffer: number;
  /** Loaded tile images by "x,y"; batched updates to reduce re-renders during pan. */
  hexTileCache: Record<string, { objectURL: string; z: number; isEmpty: boolean }>;
}

// 2 - ADD INITIAL STATE VALUE
export const blankState: AppState = {
  hash: "",
  player: null,
  booleanMap: {},
  tileZIndexes: {},
  statistic: {},
  isPlayerProfilePopupOpen: false,
  isConfirmDeletePopupOpen: false,
  isLoginPopupOpen: false,
  isHexInfoPopupOpen: false,
  isHexGeneratePopupOpen: false,
  isPlayerInfoPopupOpen: false,
  isAboutGamePopupOpen: false,
  isMenuPopupOpen: false,
  isGeneratedAwardPopupOpen: false,
  isMostViewedPopupOpen: false,
  isGlobalChatPopupOpen: false,
  isSearchPopupOpen: false,
  isRedeemPopupOpen: false,
  isReportPopupOpen: false,
  isNFTAlertPopupOpen: true,
  isNotificationsPopupOpen: false,
  isNftPopupOpen: false,
  isNFTConfirmMintingPopupOpen: false,
  selectedHexCoord: null,
  selectedHexInfo: null,
  selectedHexExtra: null,
  generatingNowHexs: [],
  regeneratingNowHexs: [],
  convertingNowHexs: [],
  videoGeneratingNowHexs: [],
  hasVideo: [],
  isWorldDragging: false,
  selectedPlayerUID: null,
  worldScale: 1,
  notifications: [],
  ratings: {
    mostViewed: [],
    mostViewedLastWeek: [],
    lastCreated: [],
    topPlayers: [],
    mostUpvotedLastWeek: [],
    highestPointsLastWeek: [],
  },
  worldX: 0,
  worldY: 0,
  isMenuLeftOpen: false,
  regeneratedHexagons: [],
  generatedHexagons: [],
  awardPopupCoordinates: null,
  awardPopupQueue: [],
  battle: null,
  isBattleFieldPopupOpen: false,
  invertMapPan: false,
  highQuality: false,
  hexGridBuffer: 0,
  hexTileCache: {},
};
blankState.hash = hash(blankState);
const coordinates = getInitialCoordinates();
blankState.worldX = coordinates.worldX;
blankState.worldY = coordinates.worldY;

// 3 - DEFINE ACTION TYPES
export enum ActionType {
  UPDATE_PLAYER = "UPDATE_PLAYER",
  DELETE_PLAYER = "DELETE_PLAYER",
  CHANGE_WORLD_COORDINATES = "CHANGE_WORLD_COORDINATES",
  SELECT_HEX = "SELECT_HEX",
  DESELECT_HEX = "DESELECT_HEX",
  ADD_SELECTED_HEX_INFO = "ADD_SELECTED_HEX_INFO",
  TOGGLE_PLAYER_PROFILE_POPUP = "TOGGLE_PLAYER_PROFILE_POPUP",
  TOGGLE_DELETE_POPUP = "TOGGLE_DELETE_POPUP",
  REMOVE_SELECTED_HEX_INFO = "REMOVE_SELECTED_HEX_INFO",
  TOGGLE_LOGIN_POPUP = "TOGGLE_LOGIN_POPUP",
  OPEN_HEX_INFO_POPUP = "OPEN_HEX_INFO_POPUP",
  CLOSE_HEX_INFO_POPUP = "CLOSE_HEX_INFO_POPUP",
  TOGGLE_HEX_GENERATE_POPUP = "TOGGLE_HEX_GENERATE_POPUP",
  CLOSE_HEX_GENERATE_POPUP = "CLOSE_HEX_GENERATE_POPUP",
  TOGGLE_GENERATED_AWARD_POPUP = "TOGGLE_GENERATED_AWARD_POPUP",
  OPEN_GENERATED_AWARD_POPUP = "OPEN_GENERATED_AWARD_POPUP",
  CLOSE_GENERATED_AWARD_POPUP = "CLOSE_GENERATED_AWARD_POPUP",
  ENQUEUE_GENERATED_AWARD_POPUP = "ENQUEUE_GENERATED_AWARD_POPUP",
  DEQUEUE_GENERATED_AWARD_POPUP = "DEQUEUE_GENERATED_AWARD_POPUP",
  TOGGLE_PLAYER_INFO_POPUP = "TOGGLE_PLAYER_INFO_POPUP",
  TOGGLE_ABOUT_GAME_POPUP = "TOGGLE_ABOUT_GAME_POPUP",
  ADD_GENERATING_NOW_HEX = "ADD_GENERATING_NOW_HEX",
  REMOVE_GENERATING_NOW_HEX = "REMOVE_GENERATING_NOW_HEX",
  ADD_REGENERATING_NOW_HEX = "ADD_REGENERATING_NOW_HEX",
  REMOVE_REGENERATING_NOW_HEX = "REMOVE_REGENERATING_NOW_HEX",
  START_WORLD_DRAGGING = "START_WORLD_DRAGGING",
  STOP_WORLD_DRAGGING = "STOP_WORLD_DRAGGING",
  SELECT_PLAYER = "SELECT_PLAYER",
  DESELECT_PLAYER = "DESELECT_PLAYER",
  TOGGLE_MENU_POPUP = "TOGGLE_MENU_POPUP",
  UPDATE_STATISTIC = "UPDATE_STATISTIC",
  ZOOM_IN = "ZOOM_IN",
  ZOOM_OUT = "ZOOM_OUT",
  TOGGLE_MOST_VIEWED_POPUP = "TOGGLE_MOST_VIEWED_POPUP",
  SET_MOST_VIEWED_RATING = "SET_MOST_VIEWED_RATING",
  SET_MOST_VIEWED_LAST_WEEK_RATING = "SET_MOST_VIEWED_LAST_WEEK_RATING",
  SET_LAST_CREATED_RATING = "SET_LAST_CREATED_RATING",
  SET_TOP_PLAYERS_RATING = "SET_TOP_PLAYERS_RATING",
  SET_MOST_UPVOTED_LAST_WEEK_RATING = "SET_MOST_UPVOTED_LAST_WEEK_RATING",
  SET_HIGHEST_POINTS_LAST_WEEK_RATING = "SET_HIGHEST_POINTS_LAST_WEEK_RATING",
  ADD_TO_BOOLEAN_MAP = "ADD_TO_BOOLEAN_MAP",
  SET_TILE_Z_INDEX = "SET_TILE_Z_INDEX",
  TOGGLE_GLOBAL_CHAT_POPUP = "TOGGLE_GLOBAL_CHAT_POPUP",
  TOGGLE_SEARCH_POPUP = "TOGGLE_SEARCH_POPUP",
  TOGGLE_REDEEM_POPUP = "TOGGLE_REDEEM_POPUP",
  TOGGLE_REPORT_POPUP = "TOGGLE_REPORT_POPUP",
  TOGGLE_SCAM_ALERT_POPUP = "TOGGLE_SCAM_ALERT_POPUP",
  UPDATE_NOTIFICATIONS = "UPDATE_NOTIFICATIONS",
  TOGGLE_NOTIFICATIONS_POPUP = "TOGGLE_NOTIFICATIONS_POPUP",
  TOGGLE_MENU_LEFT = "TOGGLE_MENU_LEFT",
  ADD_REGENERATED_HEXAGON = "ADD_REGENERATED_HEXAGON",
  ADD_GENERATED_HEXAGON = "ADD_GENERATED_HEXAGON",
  REMOVE_GENERATED_HEXAGON = "REMOVE_GENERATED_HEXAGON",
  REMOVE_REGENERATED_HEXAGON = "REMOVE_REGENERATED_HEXAGON",
  ADD_CONVERTING_NOW_HEX = "ADD_CONVERTING_NOW_HEX",
  REMOVE_CONVERTING_NOW_HEX = "REMOVE_CONVERTING_NOW_HEX",
  ADD_VIDEO_GENERATING_NOW_HEX = "ADD_VIDEO_GENERATING_NOW_HEX",
  REMOVE_VIDEO_GENERATING_NOW_HEX = "REMOVE_VIDEO_GENERATING_NOW_HEX",
  SET_HAS_VIDEO_LIST = "SET_HAS_VIDEO_LIST",
  ADD_HEX_HAS_VIDEO = "ADD_HEX_HAS_VIDEO",
  ADD_SELECTED_HEX_EXTRA = "ADD_SELECTED_HEX_EXTRA",
  REMOVE_SELECTED_HEX_EXTRA = "REMOVE_SELECTED_HEX_EXTRA",
  SET_BATTLE_STATE = "SET_BATTLE_STATE",
  SHOW_BATTLE_FIELD_POPUP = "SHOW_BATTLE_FIELD_POPUP",
  HIDE_BATTLE_FIELD_POPUP = "HIDE_BATTLE_FIELD_POPUP",
  SET_INVERT_MAP_PAN = "SET_INVERT_MAP_PAN",
  SET_HIGH_QUALITY = "SET_HIGH_QUALITY",
  SET_HEX_GRID_BUFFER = "SET_HEX_GRID_BUFFER",
  BATCH_HEX_TILES_LOADED = "BATCH_HEX_TILES_LOADED",
}

// 4 - DEFINE ACTION INTERFACE
interface ZoomInAction {
  type: ActionType.ZOOM_IN;
  value: number;
}

interface OpenHexInfoPopupAction {
  type: ActionType.OPEN_HEX_INFO_POPUP;
}

interface CloseHexInfoPopupAction {
  type: ActionType.CLOSE_HEX_INFO_POPUP;
}

interface UpdateStatisticAction {
  type: ActionType.UPDATE_STATISTIC;
  statistic: Statistic;
}

interface ToggleNotificationsPopupAction {
  type: ActionType.TOGGLE_NOTIFICATIONS_POPUP;
}

interface UpdateNotificationsAction {
  type: ActionType.UPDATE_NOTIFICATIONS;
  notifications: INotification[];
}

interface ToggleScamAlertPopupAction {
  type: ActionType.TOGGLE_SCAM_ALERT_POPUP;
}

interface ToggleGlobalChatPopupAction {
  type: ActionType.TOGGLE_GLOBAL_CHAT_POPUP;
}

interface ToggleSearchPopupAction {
  type: ActionType.TOGGLE_SEARCH_POPUP;
}

interface ToggleRedeemPopupAction {
  type: ActionType.TOGGLE_REDEEM_POPUP;
}

interface ToggleReportPopupAction {
  type: ActionType.TOGGLE_REPORT_POPUP;
}

interface AddToBooleanMapAction {
  type: ActionType.ADD_TO_BOOLEAN_MAP;
  x: number;
  y: number;
}

interface SetTileZIndexAction {
  type: ActionType.SET_TILE_Z_INDEX;
  x: number;
  y: number;
  zIndex: number;
}

interface SetMostViewedRatingAction {
  type: ActionType.SET_MOST_VIEWED_RATING;
  hexagens: Hexagen[];
}

interface SetMostViewedLastWeekRatingAction {
  type: ActionType.SET_MOST_VIEWED_LAST_WEEK_RATING;
  hexagens: Hexagen[];
}

interface SetLastCreatedRatingAction {
  type: ActionType.SET_LAST_CREATED_RATING;
  hexagens: Hexagen[];
}

interface SetTopPlayersRatingAction {
  type: ActionType.SET_TOP_PLAYERS_RATING;
  players: Player[];
}

interface SetMostUpvotedLastWeekRatingAction {
  type: ActionType.SET_MOST_UPVOTED_LAST_WEEK_RATING;
  hexagens: Hexagen[];
}

interface SetHighestPointsLastWeekRatingAction {
  type: ActionType.SET_HIGHEST_POINTS_LAST_WEEK_RATING;
  hexagens: Hexagen[];
}

interface ZoomOutAction {
  type: ActionType.ZOOM_OUT;
  value: number;
}

interface ToggleGeneratedAwardPopup {
  type: ActionType.TOGGLE_GENERATED_AWARD_POPUP;
  coordinates?: { x: number; y: number };
}

interface OpenGeneratedAwardPopupAction {
  type: ActionType.OPEN_GENERATED_AWARD_POPUP;
  coordinates: { x: number; y: number };
}

interface CloseGeneratedAwardPopupAction {
  type: ActionType.CLOSE_GENERATED_AWARD_POPUP;
}

interface EnqueueGeneratedAwardPopupAction {
  type: ActionType.ENQUEUE_GENERATED_AWARD_POPUP;
  coordinates: { x: number; y: number };
}

interface DequeueGeneratedAwardPopupAction {
  type: ActionType.DEQUEUE_GENERATED_AWARD_POPUP;
}

interface ToggleMenuPopupAction {
  type: ActionType.TOGGLE_MENU_POPUP;
}

interface ToggleAboutGamePopupAction {
  type: ActionType.TOGGLE_ABOUT_GAME_POPUP;
}

interface SelectPlayerAction {
  type: ActionType.SELECT_PLAYER;
  uid: string;
}

interface DeselectPlayerAction {
  type: ActionType.DESELECT_PLAYER;
}

interface TogglePlayerInfoPopupAction {
  type: ActionType.TOGGLE_PLAYER_INFO_POPUP;
}
interface StartWorldDraggingAction {
  type: ActionType.START_WORLD_DRAGGING;
}

interface StopWorldDraggingAction {
  type: ActionType.STOP_WORLD_DRAGGING;
}

interface AddGenegatingNowHexAction {
  type: ActionType.ADD_GENERATING_NOW_HEX;
  x: number;
  y: number;
}

interface RemoveGenegatingNowHexAction {
  type: ActionType.REMOVE_GENERATING_NOW_HEX;
  x: number;
  y: number;
}

interface AddRegeneratingNowHexAction {
  type: ActionType.ADD_REGENERATING_NOW_HEX;
  x: number;
  y: number;
}

interface RemoveRegeneratingNowHexAction {
  type: ActionType.REMOVE_REGENERATING_NOW_HEX;
  x: number;
  y: number;
}

interface ToggleHexGeneratePopupAction {
  type: ActionType.TOGGLE_HEX_GENERATE_POPUP;
}

interface CloseHexGeneratePopupAction {
  type: ActionType.CLOSE_HEX_GENERATE_POPUP;
}
interface ToggleLoginPopupAction {
  type: ActionType.TOGGLE_LOGIN_POPUP;
}

interface AddHexInfoAction {
  type: ActionType.ADD_SELECTED_HEX_INFO;
  hexInfo: Hexagen;
}

interface RemoveHexInfoAction {
  type: ActionType.REMOVE_SELECTED_HEX_INFO;
}

interface ToggleDeletePopupAction {
  type: ActionType.TOGGLE_DELETE_POPUP;
}

interface SelectHexAction {
  type: ActionType.SELECT_HEX;
  x: number;
  y: number;
}

interface DeselectHexAction {
  type: ActionType.DESELECT_HEX;
}

interface ChangeWorldCoordinatesAction {
  type: ActionType.CHANGE_WORLD_COORDINATES;
  x: number;
  y: number;
}

interface UpdatePlayerAction {
  type: ActionType.UPDATE_PLAYER;
  player: Player | null;
}

interface DeletePlayerAction {
  type: ActionType.DELETE_PLAYER;
}

interface TogglePlayerProfilePopupAction {
  type: ActionType.TOGGLE_PLAYER_PROFILE_POPUP;
}

interface SetInvertMapPanAction {
  type: ActionType.SET_INVERT_MAP_PAN;
  value: boolean;
}

interface SetHighQualityAction {
  type: ActionType.SET_HIGH_QUALITY;
  value: boolean;
}

interface SetHexGridBufferAction {
  type: ActionType.SET_HEX_GRID_BUFFER;
  value: number;
}

interface BatchHexTilesLoadedAction {
  type: ActionType.BATCH_HEX_TILES_LOADED;
  tiles: Array<{ x: number; y: number; objectURL: string | null; z: number; isEmpty: boolean }>;
}

interface ToggleMostViewedPopupAction {
  type: ActionType.TOGGLE_MOST_VIEWED_POPUP;
}

interface ToggleMenuLeftAction {
  type: ActionType.TOGGLE_MENU_LEFT;
}

interface AddRegeneratedHexagonAction {
  type: ActionType.ADD_REGENERATED_HEXAGON;
  x: number;
  y: number;
}

interface AddGeneratedHexagonAction {
  type: ActionType.ADD_GENERATED_HEXAGON;
  x: number;
  y: number;
}

interface RemoveGeneratedHexagonAction {
  type: ActionType.REMOVE_GENERATED_HEXAGON;
  x: number;
  y: number;
}

interface RemoveRegeneratedHexagonAction {
  type: ActionType.REMOVE_REGENERATED_HEXAGON;
  x: number;
  y: number;
}

interface AddConvertingNowHexAction {
  type: ActionType.ADD_CONVERTING_NOW_HEX;
  x: number;
  y: number;
}

interface RemoveConvertingNowHexAction {
  type: ActionType.REMOVE_CONVERTING_NOW_HEX;
  x: number;
  y: number;
}

interface AddVideoGeneratingNowHexAction {
  type: ActionType.ADD_VIDEO_GENERATING_NOW_HEX;
  x: number;
  y: number;
}

interface RemoveVideoGeneratingNowHexAction {
  type: ActionType.REMOVE_VIDEO_GENERATING_NOW_HEX;
  x: number;
  y: number;
}

interface SetHasVideoListAction {
  type: ActionType.SET_HAS_VIDEO_LIST;
  list: { x: number; y: number }[];
}

interface AddHexHasVideoAction {
  type: ActionType.ADD_HEX_HAS_VIDEO;
  x: number;
  y: number;
}

interface AddSelectedHexExtraAction {
  type: ActionType.ADD_SELECTED_HEX_EXTRA;
  extra: {
    hasUpvote: boolean;
    creator: Player | null;
    owner: Player | null;
  };
}

interface RemoveSelectedHexExtraAction {
  type: ActionType.REMOVE_SELECTED_HEX_EXTRA;
}

interface SetBattleStateAction {
  type: ActionType.SET_BATTLE_STATE;
  battleState: BattleState;
}

interface ShowBattleFieldPopupAction {
  type: ActionType.SHOW_BATTLE_FIELD_POPUP;
}

interface HideBattleFieldPopupAction {
  type: ActionType.HIDE_BATTLE_FIELD_POPUP;
}

// 5 - ADD ACTION INTERFACE TO UNION TYPE
export type AppAction = (
  | TogglePlayerProfilePopupAction
  | ToggleDeletePopupAction
  | ToggleLoginPopupAction
  | ToggleHexGeneratePopupAction
  | CloseHexGeneratePopupAction
  | UpdatePlayerAction
  | DeletePlayerAction
  | ChangeWorldCoordinatesAction
  | SelectHexAction
  | DeselectHexAction
  | AddHexInfoAction
  | RemoveHexInfoAction
  | AddGenegatingNowHexAction
  | RemoveGenegatingNowHexAction
  | AddRegeneratingNowHexAction
  | RemoveRegeneratingNowHexAction
  | StartWorldDraggingAction
  | StopWorldDraggingAction
  | TogglePlayerInfoPopupAction
  | SelectPlayerAction
  | DeselectPlayerAction
  | ToggleAboutGamePopupAction
  | ToggleMenuPopupAction
  | ZoomInAction
  | ZoomOutAction
  | UpdateStatisticAction
  | ToggleGeneratedAwardPopup
  | OpenGeneratedAwardPopupAction
  | CloseGeneratedAwardPopupAction
  | EnqueueGeneratedAwardPopupAction
  | DequeueGeneratedAwardPopupAction
  | ToggleMostViewedPopupAction
  | SetMostViewedRatingAction
  | SetMostViewedLastWeekRatingAction
  | SetLastCreatedRatingAction
  | SetTopPlayersRatingAction
  | AddToBooleanMapAction
  | SetTileZIndexAction
  | ToggleGlobalChatPopupAction
  | ToggleSearchPopupAction
  | ToggleRedeemPopupAction
  | ToggleReportPopupAction
  | SetMostUpvotedLastWeekRatingAction
  | SetHighestPointsLastWeekRatingAction
  | ToggleScamAlertPopupAction
  | UpdateNotificationsAction
  | ToggleNotificationsPopupAction
  | OpenHexInfoPopupAction
  | CloseHexInfoPopupAction
  | ToggleMenuLeftAction
  | AddRegeneratedHexagonAction
  | AddGeneratedHexagonAction
  | RemoveGeneratedHexagonAction
  | RemoveRegeneratedHexagonAction
  | AddConvertingNowHexAction
  | RemoveConvertingNowHexAction
  | AddVideoGeneratingNowHexAction
  | RemoveVideoGeneratingNowHexAction
  | SetHasVideoListAction
  | AddHexHasVideoAction
  | AddSelectedHexExtraAction
  | RemoveSelectedHexExtraAction
  | SetBattleStateAction
  | ShowBattleFieldPopupAction
  | HideBattleFieldPopupAction
  | SetInvertMapPanAction
  | SetHighQualityAction
  | SetHexGridBufferAction
  | BatchHexTilesLoadedAction
) & { logggable?: boolean };

// 6 - UPDATE REDUCER FUNCTION
export const reducer = (state: AppState, action: AppAction): AppState => {
  let worldScale: number;

  switch (action.type) {
    case ActionType.SET_BATTLE_STATE:
      return {
        ...state,
        battle: action.battleState,
      };
    case ActionType.SHOW_BATTLE_FIELD_POPUP:
      return {
        ...state,
        isBattleFieldPopupOpen: true,
      };
    case ActionType.HIDE_BATTLE_FIELD_POPUP:
      return {
        ...state,
        isBattleFieldPopupOpen: false,
      };
    case ActionType.SET_INVERT_MAP_PAN:
      return {
        ...state,
        invertMapPan: action.value,
      };
    case ActionType.SET_HIGH_QUALITY:
      return {
        ...state,
        highQuality: action.value,
      };
    case ActionType.SET_HEX_GRID_BUFFER:
      return {
        ...state,
        hexGridBuffer: Math.min(2, Math.max(0, action.value)),
      };
    case ActionType.ADD_CONVERTING_NOW_HEX:
      return {
        ...state,
        convertingNowHexs: [
          ...state.convertingNowHexs,
          { x: action.x, y: action.y },
        ],
      };
    case ActionType.REMOVE_CONVERTING_NOW_HEX:
      return {
        ...state,
        convertingNowHexs: state.convertingNowHexs.filter(
          (hex) => !(hex.x === action.x && hex.y === action.y)
        ),
      };
    case ActionType.ADD_VIDEO_GENERATING_NOW_HEX:
      return {
        ...state,
        videoGeneratingNowHexs: [
          ...state.videoGeneratingNowHexs,
          { x: action.x, y: action.y },
        ],
      };
    case ActionType.REMOVE_VIDEO_GENERATING_NOW_HEX:
      return {
        ...state,
        videoGeneratingNowHexs: state.videoGeneratingNowHexs.filter(
          (hex) => !(hex.x === action.x && hex.y === action.y)
        ),
      };
    case ActionType.SET_HAS_VIDEO_LIST: {
      const prevLen = (state.hasVideo || []).length;
      const newLen = (action.list || []).length;
      videoDebugLog("reducer SET_HAS_VIDEO_LIST", { prevLen, newLen });
      if (newLen < prevLen) {
        videoDebugLog("LIST SHRANK!", { prevLen, newLen });
      }
      return {
        ...state,
        hasVideo: action.list,
      };
    }
    case ActionType.ADD_HEX_HAS_VIDEO: {
      const list = state.hasVideo || [];
      const exists = list.some((h) => h.x === action.x && h.y === action.y);
      if (exists) return state;
      return { ...state, hasVideo: [...list, { x: action.x, y: action.y }] };
    }
    case ActionType.TOGGLE_MOST_VIEWED_POPUP:
      return { ...state, isMostViewedPopupOpen: !state.isMostViewedPopupOpen };
    case ActionType.ADD_REGENERATED_HEXAGON:
      return {
        ...state,
        regeneratedHexagons: [
          ...state.regeneratedHexagons.filter(
            (hex) => !(hex.x === action.x && hex.y === action.y)
          ),
          { x: action.x, y: action.y, createdAt: Date.now() },
        ],
      };
    case ActionType.ADD_GENERATED_HEXAGON:
      return {
        ...state,
        generatedHexagons: [
          ...state.generatedHexagons,
          { x: action.x, y: action.y, createdAt: Date.now() },
        ],
      };
    case ActionType.REMOVE_GENERATED_HEXAGON:
      return {
        ...state,
        generatedHexagons: state.generatedHexagons.filter(
          (hex) => !(hex.x === action.x && hex.y === action.y)
        ),
      };
    case ActionType.REMOVE_REGENERATED_HEXAGON:
      return {
        ...state,
        regeneratedHexagons: state.regeneratedHexagons.filter(
          (hex) => !(hex.x === action.x && hex.y === action.y)
        ),
      };
    case ActionType.TOGGLE_NOTIFICATIONS_POPUP:
      return {
        ...state,
        isNotificationsPopupOpen: !state.isNotificationsPopupOpen,
      };
    case ActionType.SET_MOST_VIEWED_RATING:
      return {
        ...state,
        ratings: { ...state.ratings, mostViewed: action.hexagens },
      };
    case ActionType.TOGGLE_SCAM_ALERT_POPUP:
      return {
        ...state,
        isNFTAlertPopupOpen: !state.isNFTAlertPopupOpen,
      };
    case ActionType.UPDATE_STATISTIC:
      return { ...state, statistic: action.statistic };
    case ActionType.ADD_TO_BOOLEAN_MAP:
      return {
        ...state,
        booleanMap: {
          ...state.booleanMap,
          [`${action.x}-${action.y}`]: true,
        },
      };
    case ActionType.SET_TILE_Z_INDEX:
      return {
        ...state,
        tileZIndexes: {
          ...state.tileZIndexes,
          [`${action.x}-${action.y}`]: action.zIndex,
        },
      };
    case ActionType.BATCH_HEX_TILES_LOADED: {
      const nextCache = { ...state.hexTileCache };
      const nextTileZIndexes = { ...state.tileZIndexes };
      const nextBooleanMap = { ...state.booleanMap };
      for (const t of action.tiles) {
        const key = `${t.x}-${t.y}`;
        nextCache[key] = {
          objectURL: t.objectURL ?? "",
          z: t.z,
          isEmpty: t.isEmpty,
        };
        nextTileZIndexes[key] = t.z;
        if (!t.isEmpty) nextBooleanMap[key] = true;
      }
      return {
        ...state,
        hexTileCache: nextCache,
        tileZIndexes: nextTileZIndexes,
        booleanMap: nextBooleanMap,
      };
    }
    case ActionType.SET_MOST_VIEWED_LAST_WEEK_RATING:
      return {
        ...state,
        ratings: { ...state.ratings, mostViewedLastWeek: action.hexagens },
      };
    case ActionType.SET_LAST_CREATED_RATING:
      return {
        ...state,
        ratings: { ...state.ratings, lastCreated: action.hexagens },
      };
    case ActionType.SET_TOP_PLAYERS_RATING:
      return {
        ...state,
        ratings: { ...state.ratings, topPlayers: action.players },
      };
    case ActionType.SET_MOST_UPVOTED_LAST_WEEK_RATING:
      return {
        ...state,
        ratings: { ...state.ratings, mostUpvotedLastWeek: action.hexagens },
      };
    case ActionType.SET_HIGHEST_POINTS_LAST_WEEK_RATING:
      console.log("action", action);
      return {
        ...state,
        ratings: { ...state.ratings, highestPointsLastWeek: action.hexagens },
      };
    case ActionType.ZOOM_IN:
      const newZoom = state.worldScale + action.value;
      worldScale = newZoom > 1.3 ? 1.3 : newZoom;

      const coords = calculateNewWorldCoordinates(state, worldScale);

      return {
        ...state,
        worldScale,
        worldX: coords.worldX,
        worldY: coords.worldY,
      };
    case ActionType.ZOOM_OUT:
      const minZoom =
        state.player?.points && state.player.points < 1000 ? 1.0 : 0.7;
      const newZoomOut = state.worldScale - action.value;
      worldScale = newZoomOut < minZoom ? minZoom : newZoomOut;

      const coords2 = calculateNewWorldCoordinates(state, worldScale);

      return {
        ...state,
        worldScale,
        worldX: coords2.worldX,
        worldY: coords2.worldY,
      };
    case ActionType.UPDATE_PLAYER:
      return { ...state, player: action.player };
    case ActionType.SELECT_PLAYER:
      return { ...state, selectedPlayerUID: action.uid };
    case ActionType.DESELECT_PLAYER:
      return { ...state, selectedPlayerUID: null };
    case ActionType.TOGGLE_MENU_POPUP:
      return { ...state, isMenuPopupOpen: !state.isMenuPopupOpen };
    case ActionType.TOGGLE_GLOBAL_CHAT_POPUP:
      return {
        ...state,
        isGlobalChatPopupOpen: !state.isGlobalChatPopupOpen,
      };
    case ActionType.TOGGLE_SEARCH_POPUP:
      return {
        ...state,
        isSearchPopupOpen: !state.isSearchPopupOpen,
      };
    case ActionType.TOGGLE_PLAYER_INFO_POPUP:
      return {
        ...state,
        isPlayerInfoPopupOpen: !state.isPlayerInfoPopupOpen,
      };
    case ActionType.DELETE_PLAYER:
      return { ...state, player: null };
    case ActionType.TOGGLE_PLAYER_PROFILE_POPUP:
      return {
        ...state,
        isPlayerProfilePopupOpen: !state.isPlayerProfilePopupOpen,
      };
    case ActionType.CHANGE_WORLD_COORDINATES:
      return { ...state, worldX: action.x, worldY: action.y };
    case ActionType.SELECT_HEX:
      return {
        ...state,
        selectedHexCoord: { x: action.x, y: action.y },
        isGeneratedAwardPopupOpen: false,
      };
    case ActionType.DESELECT_HEX:
      return { ...state, selectedHexCoord: null };
    case ActionType.TOGGLE_DELETE_POPUP:
      return {
        ...state,
        isConfirmDeletePopupOpen: !state.isConfirmDeletePopupOpen,
      };
    case ActionType.ADD_SELECTED_HEX_INFO:
      return { ...state, selectedHexInfo: action.hexInfo };
    case ActionType.REMOVE_SELECTED_HEX_INFO:
      return { ...state, selectedHexInfo: null };
    case ActionType.ADD_SELECTED_HEX_EXTRA:
      return { ...state, selectedHexExtra: action.extra };
    case ActionType.REMOVE_SELECTED_HEX_EXTRA:
      return { ...state, selectedHexExtra: null };
    case ActionType.TOGGLE_LOGIN_POPUP:
      return { ...state, isLoginPopupOpen: !state.isLoginPopupOpen };
    case ActionType.TOGGLE_GENERATED_AWARD_POPUP:
      return {
        ...state,
        isGeneratedAwardPopupOpen: !state.isGeneratedAwardPopupOpen,
        awardPopupCoordinates: action.coordinates || null,
      };
    case ActionType.OPEN_GENERATED_AWARD_POPUP:
      return {
        ...state,
        isGeneratedAwardPopupOpen: true,
        awardPopupCoordinates: action.coordinates,
      };
    case ActionType.CLOSE_GENERATED_AWARD_POPUP:
      return {
        ...state,
        isGeneratedAwardPopupOpen: false,
      };
    case ActionType.ENQUEUE_GENERATED_AWARD_POPUP:
      return {
        ...state,
        awardPopupQueue: [...state.awardPopupQueue, action.coordinates],
      };
    case ActionType.DEQUEUE_GENERATED_AWARD_POPUP:
      return {
        ...state,
        awardPopupQueue: state.awardPopupQueue.slice(1),
      };
    case ActionType.OPEN_HEX_INFO_POPUP:
      return { ...state, isHexInfoPopupOpen: true };
    case ActionType.CLOSE_HEX_INFO_POPUP:
      return { ...state, isHexInfoPopupOpen: false };
    case ActionType.TOGGLE_HEX_GENERATE_POPUP:
      return {
        ...state,
        isHexGeneratePopupOpen: !state.isHexGeneratePopupOpen,
      };
    case ActionType.TOGGLE_ABOUT_GAME_POPUP:
      return {
        ...state,
        isAboutGamePopupOpen: !state.isAboutGamePopupOpen,
      };
    case ActionType.TOGGLE_REDEEM_POPUP:
      return { ...state, isRedeemPopupOpen: !state.isRedeemPopupOpen };
    case ActionType.TOGGLE_REPORT_POPUP:
      return { ...state, isReportPopupOpen: !state.isReportPopupOpen };
    case ActionType.ADD_GENERATING_NOW_HEX:
      return {
        ...state,
        generatingNowHexs: [
          ...state.generatingNowHexs,
          { x: action.x, y: action.y },
        ],
      };
    case ActionType.REMOVE_GENERATING_NOW_HEX:
      return {
        ...state,
        generatingNowHexs: state.generatingNowHexs.filter(
          (hex) => !(hex.x === action.x && hex.y === action.y)
        ),
      };
    case ActionType.ADD_REGENERATING_NOW_HEX:
      return {
        ...state,
        regeneratingNowHexs: [
          ...state.regeneratingNowHexs,
          { x: action.x, y: action.y },
        ],
        regeneratedHexagons: state.regeneratedHexagons.filter(
          (hex) => !(hex.x === action.x && hex.y === action.y)
        ),
      };
    case ActionType.REMOVE_REGENERATING_NOW_HEX:
      return {
        ...state,
        regeneratingNowHexs: state.regeneratingNowHexs.filter(
          (hex) => !(hex.x === action.x && hex.y === action.y)
        ),
      };
    case ActionType.START_WORLD_DRAGGING:
      return { ...state, isWorldDragging: true };
    case ActionType.STOP_WORLD_DRAGGING:
      return { ...state, isWorldDragging: false };
    case ActionType.UPDATE_NOTIFICATIONS:
      return { ...state, notifications: action.notifications };
    case ActionType.TOGGLE_MENU_LEFT:
      return {
        ...state,
        isMenuLeftOpen: !state.isMenuLeftOpen,
      };
    case ActionType.CLOSE_HEX_GENERATE_POPUP:
      return {
        ...state,
        isHexGeneratePopupOpen: false,
      };
    default:
      return state;
  }
};

// DONE!
